// ConfirmationCard — one pending (or decided) write/destructive action a
// chat turn wants to take (docs/plans/2026-09-30-runners/07-chat.md §2.3,
// §2.8), created by the worker's own ChatGuardService against the shared
// ChatConfirmationPayload shape.

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { ChatConfirmationPayload, ChatMessage } from '@goblic/shared'
import { chatApi } from '../../lib/api-client'
import { Markdown } from '../markdown'

const STATUS_LABEL: Record<ChatConfirmationPayload['status'], string> = {
  pending: 'Waiting for you',
  approved: 'Confirmed',
  rejected: 'Rejected',
  expired: 'Expired — not confirmed in time',
}

function isBatchCallsArray(
  args: unknown
): args is { calls: Array<{ tool: string; args: object }> } {
  if (typeof args !== 'object' || args === null) return false
  const obj = args as Record<string, unknown>
  if (!('calls' in obj) || !Array.isArray(obj.calls)) return false
  return obj.calls.every(
    (item: unknown) =>
      typeof item === 'object' &&
      item !== null &&
      'tool' in item &&
      typeof (item as Record<string, unknown>).tool === 'string'
  )
}

function truncateJson(obj: unknown, maxLength: number = 80): string {
  const json = JSON.stringify(obj ?? {})
  return json.length > maxLength ? json.slice(0, maxLength) + '…' : json
}

export function ConfirmationCard({
  conversationId,
  message,
}: {
  conversationId: string
  message: ChatMessage
}) {
  const payload = message.payload as unknown as ChatConfirmationPayload
  const [note, setNote] = useState('')
  const queryClient = useQueryClient()

  const decide = useMutation({
    mutationFn: (decision: 'confirm' | 'reject') =>
      chatApi.confirm(conversationId, message.id, {
        decision,
        note: note.trim() || undefined,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['chat-message', message.id, message.updatedAt], updated)
      void queryClient.invalidateQueries({ queryKey: ['chat-status', conversationId] })
      void queryClient.invalidateQueries({
        queryKey: ['chat-conversation', conversationId],
      })
    },
  })

  const isDestructive = payload.effect === 'destructive'
  const isPending = payload.status === 'pending'

  return (
    <div
      className={`rounded-control border p-3.5 text-base ${
        isDestructive
          ? 'border-danger/40 bg-danger-soft'
          : 'border-signal/30 bg-signal-soft'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display text-md font-semibold text-ink">
            {payload.tool}
          </p>
          {payload.reason ? (
            <div className="mt-0.5 break-words text-sm text-ink-2">
              <Markdown>{payload.reason}</Markdown>
            </div>
          ) : null}
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${
            isDestructive
              ? 'bg-danger text-surface'
              : 'bg-signal text-surface'
          }`}
        >
          {payload.effect}
        </span>
      </div>

      {payload.args != null ? (
        isBatchCallsArray(payload.args) ? (
          <div className="mt-2.5 space-y-2.5">
            <div className="max-h-60 overflow-auto rounded-control bg-ink/5 p-2.5 text-sm text-ink-2">
              <div className="space-y-1.5 font-mono">
                {payload.args.calls.map((call, index) => (
                  <div key={index} className="flex gap-2 text-xs">
                    <span className="shrink-0 w-6 text-right font-semibold text-ink-3">
                      {index + 1}.
                    </span>
                    <span className="shrink-0 font-semibold text-ink">{call.tool}</span>
                    <span className="truncate text-ink-2">
                      {truncateJson(call.args)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <details className="text-sm">
              <summary className="cursor-pointer font-medium text-ink-2 hover:text-ink">
                Show raw JSON
              </summary>
              <pre className="mt-2.5 max-h-40 overflow-auto rounded-control bg-ink/5 p-2.5 font-mono text-sm text-ink-2">
                {JSON.stringify(payload.args, null, 2)}
              </pre>
            </details>
          </div>
        ) : (
          <pre className="mt-2.5 max-h-40 overflow-auto rounded-control bg-ink/5 p-2.5 font-mono text-sm text-ink-2">
            {JSON.stringify(payload.args, null, 2)}
          </pre>
        )
      ) : null}

      {isPending ? (
        <div className="mt-3 space-y-2.5">
          <input
            type="text"
            className="field-input"
            placeholder="Optional note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            disabled={decide.isPending}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn-primary btn-sm"
              disabled={decide.isPending || message.truncated === true}
              onClick={() => decide.mutate('confirm')}
            >
              Confirm
            </button>
            <button
              type="button"
              className="btn-danger-quiet btn-sm"
              disabled={decide.isPending}
              onClick={() => decide.mutate('reject')}
            >
              Reject
            </button>
          </div>
          {decide.isError ? (
            <p className="text-sm text-danger">
              {decide.error instanceof Error ? decide.error.message : 'Could not record your decision.'}
            </p>
          ) : null}
        </div>
      ) : (
        <p className="mt-3 text-sm font-medium text-ink-2">
          {STATUS_LABEL[payload.status]}
          {payload.note ? ` — “${payload.note}”` : ''}
        </p>
      )}
    </div>
  )
}
